import React, { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import {
    Box,
    Typography,
    TextField,
    Button,
    Paper,
    FormControl,
    InputLabel,
    Select as MuiSelect, // Renamed to avoid conflict with react-select
    MenuItem,
    Grid,
    CircularProgress,
    Chip,
    Avatar,
    IconButton
} from "@mui/material";

import {
    Article as ArticleIcon,
    Create as CreateIcon,
    Image as ImageIcon,
    AccessTime as AccessTimeIcon,
    Category as CategoryIcon,
    Label as LabelIcon,
    Publish as PublishIcon,
    Drafts as DraftsIcon,
    Close as CloseIcon,
    AddPhotoAlternate as AddPhotoAlternateIcon,
    DeleteForever as DeleteForeverIcon
} from "@mui/icons-material";

import Select from 'react-select';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { getWithTokenAPI, postWithTokenAPI, postWithoutTokenAPI } from "../../utils/Api";
import { modules, formats } from "../../utils/QuillConfig"; // Assuming these are correctly configured for ReactQuill
import { FaAngleLeft } from "react-icons/fa";


// Colors (retained for consistency)
const primaryGreen = "#DC3545";
const lightGreen = "#ffe5e5";
const darkGreen = "#b02a37";

const Blogmanagementcreate = () => {
    const token = localStorage.getItem("token");
    let navigate = useNavigate();

    const BlogService = {
        create: async (token, blogData) => {
            return await postWithTokenAPI(token, "/blog/create", JSON.stringify(blogData), true);
        },
        fetchAll: async (token, filter = "All") => {
            return await getWithTokenAPI(token, `/blog/find-all?filterBy=${filter}`);
        },
    };

    const MetaService = {
        fetchTags: async (token) => {
            return await getWithTokenAPI(token, "/tag/find-all");
        },
        fetchCategories: async (token) => {
            return await getWithTokenAPI(token, "/category/find-all");
        }
    };

    const [state, setState] = useState({
        blogs: [],
        loading: false,
        error: null,
        isLoading: false,
        tags: [],
        categories: [],
        previewImages: [],
    });

    const [blogForm, setBlogForm] = useState({
        title: "",
        subtitle: "",
        description: "",
        images: [],
        blog_status: "published",
        tags: [],
        categories: [],
        read_time: "",
        created_by: "",
        __v: 0,
        Approval_status: "Approval"
    });

    const updateState = (newState) => {
        setState(prev => ({ ...prev, ...newState }));
    };

    const handleCreateBlog = async (e) => {
        e.preventDefault();
        updateState({ isLoading: true });

        try {
            const blogData = {
                ...blogForm,
                images: blogForm.images,
                created_by: localStorage.getItem("userId") || "",
            };

            const response = await BlogService.create(token, blogData);
            if (response?.statusCode === 200 || response?.success) {
                toast.success("Blog created successfully!", { autoClose: 1000 })
                setTimeout(() => {
                    navigate("/backoffice/blogmanagement");
                }, 1000);
            } else {
                alert("Failed to create blog: " + (response?.message || "Unknown error"))
                toast.error("Failed to create blog: " + (response?.message || "Unknown error"));
            }
        } catch (error) {
            console.error("Error creating blog:", error);
            toast.error("Error creating blog: " + error.message);
        } finally {
            updateState({ isLoading: false });
        }
    };

    const fetchTagsAndCategories = async () => {
        try {
            const [tagsRes, catRes] = await Promise.all([
                MetaService.fetchTags(token),
                MetaService.fetchCategories(token)
            ]);

            const newState = {};
            if (tagsRes?.statusCode === 200) {
                newState.tags = tagsRes.data.tags.map(tag => ({
                    value: tag._id,
                    label: tag.name
                }));
            } else {
                toast.error("Failed to load tags: " + (tagsRes?.message || ""));
            }
            if (catRes?.statusCode === 200) {
                newState.categories = catRes.data.categories.map(category => ({
                    value: category._id,
                    label: category.category_name
                }));
            } else {
                toast.error("Failed to load categories: " + (catRes?.message || ""));
            }
            updateState(newState);
        } catch (err) {
            console.error("Error fetching metadata", err);
            toast.error("Error fetching tags and categories.");
        }
    };

    useEffect(() => {
        fetchTagsAndCategories();
    }, [token]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setBlogForm(prev => ({ ...prev, [name]: value }));
    };

    const handleDescriptionChange = (value) => {
        setBlogForm(prev => ({ ...prev, description: value }));
    };

    const getSelectedOptions = (options, values) => {
        return options.filter(option => values.includes(option.value));
    };

    const handleTagsChange = (selectedOptions) => {
        setBlogForm(prev => ({
            ...prev,
            tags: selectedOptions ? selectedOptions.map(option => option.value) : []
        }));
    };

    const handleCategoriesChange = (selectedOptions) => {
        setBlogForm(prev => ({
            ...prev,
            categories: selectedOptions ? selectedOptions.map(option => option.value) : []
        }));
    };

    const handleImageUpload = async (e) => {
        const files = Array.from(e.target.files);
        if (files.length === 0) return;

        updateState({ isLoading: true });

        const formData = new FormData();
        files.forEach(file => {
            formData.append('files', file);
        });
        formData.append('path', 'media/blogs/');

        try {
            const response = await postWithoutTokenAPI('/files/upload', formData);

            if (response.error) {
                throw new Error(response.error);
            } else if (response?.statusCode === 200 && response.data) {
                const uploadedUrls = response.data.map(item => item.file_url);
                setBlogForm(prev => ({
                    ...prev,
                    images: [...prev.images, ...uploadedUrls]
                }));
                updateState(prev => ({
                    ...prev,
                    previewImages: [...prev.previewImages, ...uploadedUrls]
                }));
                toast.success("Image uploaded successfully!");
            } else {
                toast.error("Failed to upload images: " + (response?.message || "Unknown error"));
            }
        } catch (error) {
            console.error('Error uploading images:', error);
            toast.error('Failed to upload images. Please try again.');
        } finally {
            updateState({ isLoading: false });
            e.target.value = null;
        }
    };

    const handleDeleteImage = (urlToDelete) => {
        setBlogForm(prev => ({
            ...prev,
            images: prev.images.filter(url => url !== urlToDelete)
        }));
        updateState(prev => ({
            ...prev,
            previewImages: prev.previewImages.filter(url => url !== urlToDelete)
        }));
        toast.info("Image removed from selection.");
    };

    return (
        <Box sx={{ p: 3, width: "100%" }}>
            {/* Header */}
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: "space-between",
                mb: 3,
                p: 2,
                backgroundColor: lightGreen,
                borderRadius: 2,
                borderLeft: `5px solid ${primaryGreen}`
            }}>
                <Box sx={{ display: "flex", alignItems: 'center', }}>
                    <CreateIcon sx={{ color: primaryGreen, fontSize: 40, mr: 2 }} />
                    <Box>
                        <Typography variant="h4" component="h1" sx={{ color: primaryGreen, fontWeight: 'bold' }}>
                            Create New Blog Post
                        </Typography>
                        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                            Fill in the details to add a new blog entry.
                        </Typography>
                    </Box>
                </Box>
                <Box sx={{ display: "flex", alignItems: 'center', cursor: 'pointer', color: primaryGreen }}>
                    <FaAngleLeft />
                    <Typography variant="h6" sx={{ fontWeight: 'bold', width: '150px' }}
                        onClick={() => navigate("/backoffice/blogmanagement")}>
                        back
                    </Typography>
                </Box>
            </Box>

            <Paper elevation={3} sx={{ p: 4, borderRadius: 2 }}>
                <form onSubmit={handleCreateBlog} className="blogform">
                 <Grid container spacing={3}>
    {/* First Row - Title and Subtitle */}
    <Grid item xs={12} md={6}>
        <InputLabel className="mb-2">Title <span className="text-danger">*</span></InputLabel>
        <TextField
            fullWidth
            name="title"
            value={blogForm.title}
            onChange={handleInputChange}
            required
        />
    </Grid>

    <Grid item xs={12} md={6}>
        <InputLabel className="mb-2">Subtitle <span className="text-danger">*</span></InputLabel>
        <TextField
            fullWidth
            name="subtitle"
            value={blogForm.subtitle}
            onChange={handleInputChange}
        />
    </Grid>

    {/* Second Row - Read Time, Status, and Image */}
    <Grid item xs={12} md={4}>
        <InputLabel className="mb-2">
            Read Time <span className="text-danger">*</span>
        </InputLabel>
        <TextField
            fullWidth
            type="number"
            name="read_time"
            value={blogForm.read_time}
            onChange={(e) => {
                const value = Number(e.target.value);
                if (value >= 1 || e.target.value === "") {
                    handleInputChange(e);
                }
            }}
            required
        />
    </Grid>

    <Grid item xs={12} md={4}>
        <InputLabel className="mb-2">Status <span className="text-danger">*</span></InputLabel>
        <FormControl fullWidth required>
            <MuiSelect
                name="blog_status"
                value={blogForm.blog_status}
                onChange={handleInputChange}
                startAdornment={
                    blogForm.blog_status === "published" ?
                        <PublishIcon sx={{ mr: 1, color: primaryGreen }} /> :
                        <DraftsIcon sx={{ mr: 1, color: '#e65100' }} />
                }
            >
                <MenuItem value="published">Published</MenuItem>
                <MenuItem value="draft">Unpublished</MenuItem>
            </MuiSelect>
        </FormControl>
    </Grid>

    <Grid item xs={12} md={4}>
        <InputLabel>Image <span className="text-danger">*</span></InputLabel>
        <Button
            component="label"
            variant="outlined"
            startIcon={<AddPhotoAlternateIcon />}
            sx={{ mt: 1, borderColor: primaryGreen, width: "100%", color: primaryGreen, '&:hover': { borderColor: darkGreen } }}
            disabled={state.isLoading}
        >
            Upload Image
            <input
                type="file"
                hidden
                multiple
                onChange={handleImageUpload}
                accept="image/*"
            />
        </Button>
        {state.isLoading && (
            <CircularProgress size={24} sx={{ ml: 2, color: primaryGreen }} />
        )}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 2 }}>
            {state.previewImages.map((imgUrl, index) => (
                <Box key={index} sx={{ position: 'relative', width: 100, height: 100, border: '1px solid #ddd', borderRadius: 1, overflow: 'hidden' }}>
                    <img
                        src={imgUrl}
                        alt={`Preview ${index}`}
                        style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    />
                    <IconButton
                        size="small"
                        sx={{
                            position: 'absolute',
                            top: 2,
                            right: 2,
                            backgroundColor: 'rgba(255, 0, 0, 0.7)',
                            color: 'white',
                            '&:hover': { backgroundColor: 'red' },
                        }}
                        onClick={() => handleDeleteImage(imgUrl)}
                    >
                        <CloseIcon fontSize="small" />
                    </IconButton>
                </Box>
            ))}
        </Box>
    </Grid>

    {/* Description (unchanged) */}
    <Grid item xs={12} sx={{ width: "100%", mb: 2 }} >
        <Typography variant="subtitle1" gutterBottom sx={{ mb: 1, color: 'text.secondary' }}>
            Description <span className="text-danger">*</span>
        </Typography>
        <ReactQuill
            theme="snow"
            value={blogForm.description}
            onChange={handleDescriptionChange}
            modules={modules}
            formats={formats}
            required
            style={{ height: "200px" }}
        />
    </Grid>
</Grid>


                    <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 5 }}>
                        <Button
                            variant="outlined"
                            onClick={() => navigate("/backoffice/blogmanagement")}
                            sx={{ color: primaryGreen, borderColor: primaryGreen, '&:hover': { borderColor: primaryGreen } }}
                        >
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            variant="contained"
                            startIcon={state.isLoading ? <CircularProgress size={20} color="inherit" /> : null}
                            disabled={state.isLoading || blogForm.images.length === 0}
                            sx={{
                                backgroundColor: primaryGreen,
                                color: 'white',
                                '&:hover': { backgroundColor: darkGreen }
                            }}
                        >
                            {state.isLoading ? "Adding Blog..." : "Add Blog"}
                        </Button>
                    </Grid>
                </form>
            </Paper>
            <ToastContainer position="top-center" autoClose={1500} />
        </Box>
    );
};

export default Blogmanagementcreate;